import React, { useState, useEffect, useRef } from 'react';
import { 
  Bot, Sparkles, Send, RefreshCw, Download, FileText, Check, Copy, 
  ArrowLeft, Home, Upload, Trash2, ShieldCheck, AlertCircle, FileSpreadsheet,
  Lock, CreditCard, Layers, Eye, Zap
} from 'lucide-react';
import { Language, Jurisdiction, Currency } from '../utils/i18n';
import { RegionalPaymentModal } from './RegionalPaymentModal';
import ErrorBoundary from './ErrorBoundary';

export interface DynamicAgentField {
  id: string;
  labelUk: string;
  labelPl?: string;
  labelEn?: string;
  labelDe?: string;
  labelFr?: string;
  placeholderUk?: string;
  placeholderPl?: string;
  placeholderEn?: string;
  placeholderDe?: string;
  placeholderFr?: string;
  type: 'text' | 'textarea' | 'select';
  options?: string[];
}

export interface DynamicAgentConfig {
  id: string;
  nameUk: string;
  namePl?: string;
  nameEn?: string;
  nameDe?: string;
  nameFr?: string;
  descriptionUk: string;
  descriptionPl?: string;
  descriptionEn?: string;
  descriptionDe?: string;
  descriptionFr?: string;
  categoryUk?: string;
  categoryPl?: string;
  categoryEn?: string;
  categoryDe?: string;
  categoryFr?: string;
  icon?: string;
  color?: string;
  systemPrompt: string;
  fields: DynamicAgentField[];
  demoPreset?: Record<string, string>;
  createdAt?: string;
  isCustomOnTheFly?: boolean;
}

interface DynamicOnTheFlyAgentViewProps {
  agent: DynamicAgentConfig;
  language: Language;
  currentUserEmail?: string;
  onBackToHub?: () => void;
  onGoHome?: () => void;
  hubTitle?: string;
}

export const DynamicOnTheFlyAgentView: React.FC<DynamicOnTheFlyAgentViewProps> = ({
  agent,
  language,
  currentUserEmail,
  onBackToHub,
  onGoHome,
  hubTitle
}) => {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isDe = language === 'de';
  const isFr = language === 'fr';
  const isEn = language === 'en';

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; size: number; content: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [report, setReport] = useState<string | null>(null);
  const [reportTitle, setReportTitle] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [isDemoLoaded, setIsDemoLoaded] = useState(false);

  // Freemium / Paywall state: 1st run free, subsequent runs require payment
  const [runsCount, setRunsCount] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('drh_dynamic_agent_runs_count');
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadingSteps = isDe
    ? ['🔍 Analysiere Ihre Parameter...', '⚖️ Prüfe regulatorische Normen...', '📑 Berechne Modelle & Risiken...', '🚀 Erstelle offiziellen Bericht...']
    : isFr
    ? ['🔍 Analyse de vos paramètres...', '⚖️ Vérification des normes applicables...', '📑 Calcul des matrices et risques...', '🚀 Génération du rapport officiel...']
    : isPl
    ? ['🔍 Analiza wprowadzonych danych...', '⚖️ Weryfikacja norm i przepisów...', '📑 Obliczanie macierzy ryzyka...', '🚀 Generowanie oficjalnego raportu...']
    : isUk
    ? ['🔍 Аналіз введених параметрів...', '⚖️ Перевірка нормативів та регламентів...', '📑 Розрахунок матриці ризиків...', '🚀 Генерація офіційного висновку...']
    : ['🔍 Analyzing input parameters...', '⚖️ Checking regulatory standards...', '📑 Computing matrices & risk score...', '🚀 Generating official report...'];

  useEffect(() => {
    let timer: any;
    if (isLoading) {
      timer = setInterval(() => {
        setLoadingStepIndex((prev) => (prev + 1) % loadingSteps.length);
      }, 2200);
    } else {
      setLoadingStepIndex(0);
    }
    return () => clearInterval(timer);
  }, [isLoading, loadingSteps.length]);

  const getAgentName = () => {
    if (isDe && agent.nameDe) return agent.nameDe;
    if (isFr && agent.nameFr) return agent.nameFr;
    if (isPl && agent.namePl) return agent.namePl;
    if (isEn && agent.nameEn) return agent.nameEn;
    return agent.nameUk || 'Персональний ШІ-Модуль';
  };

  const getAgentDescription = () => {
    if (isDe && agent.descriptionDe) return agent.descriptionDe;
    if (isFr && agent.descriptionFr) return agent.descriptionFr;
    if (isPl && agent.descriptionPl) return agent.descriptionPl;
    if (isEn && agent.descriptionEn) return agent.descriptionEn;
    return agent.descriptionUk || 'Спеціалізований модуль, згенерований за вашим запитом.';
  };

  const getFieldLabel = (field: DynamicAgentField) => {
    if (isDe && field.labelDe) return field.labelDe;
    if (isFr && field.labelFr) return field.labelFr;
    if (isPl && field.labelPl) return field.labelPl;
    if (isEn && field.labelEn) return field.labelEn;
    return field.labelUk;
  };

  const getFieldPlaceholder = (field: DynamicAgentField) => {
    if (isDe && field.placeholderDe) return field.placeholderDe;
    if (isFr && field.placeholderFr) return field.placeholderFr;
    if (isPl && field.placeholderPl) return field.placeholderPl;
    if (isEn && field.placeholderEn) return field.placeholderEn;
    return field.placeholderUk || '';
  };

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleFillDemo = () => {
    if (!agent.demoPreset) return;
    const filled: Record<string, string> = {};
    (agent.fields || []).forEach((field) => {
      const keyUk = `${field.id}Uk`;
      const keyPl = `${field.id}Pl`;
      const keyEn = `${field.id}En`;
      const key = field.id;

      if (isPl && agent.demoPreset?.[keyPl]) {
        filled[field.id] = agent.demoPreset[keyPl];
      } else if (isEn && agent.demoPreset?.[keyEn]) {
        filled[field.id] = agent.demoPreset[keyEn];
      } else if (agent.demoPreset?.[keyUk]) {
        filled[field.id] = agent.demoPreset[keyUk];
      } else if (agent.demoPreset?.[key]) {
        filled[field.id] = agent.demoPreset[key];
      } else {
        filled[field.id] = 'Тестові вихідні дані для розрахунку';
      }
    });
    setFormData(filled);
    setIsDemoLoaded(true);
  };

  const handleClear = () => {
    setFormData({});
    setAttachedFiles([]);
    setReport(null);
    setError(null);
    setIsDemoLoaded(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 25 * 1024 * 1024) {
        alert(isUk ? `Файл ${file.name} завеликий (максимум 25 МБ)` : `File ${file.name} is too large (>25MB)`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const extractRes = await fetch('/api/extract-text', {
          method: 'POST',
          body: formDataUpload
        });

        let textContent = '';
        if (extractRes.ok) {
          const extractData = await extractRes.json();
          textContent = extractData.text || '';
        } else {
          textContent = `[Файл ${file.name}, розмір ${file.size} байт]`;
        }

        setAttachedFiles((prev) => [
          ...prev,
          {
            name: file.name,
            size: file.size,
            content: textContent.substring(0, 40000)
          }
        ]);
      } catch (err) {
        console.error('Error uploading file:', err);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExecute = async () => {
    // Check paywall: 1st run is free!
    if (runsCount >= 1 && !currentUserEmail) {
      setIsPaymentModalOpen(true);
      return;
    }

    // Pre-flight validation
    const hasAnyInput = Object.values(formData).some((v) => v && v.trim().length > 0) || attachedFiles.length > 0;
    if (!hasAnyInput) {
      setError(
        isUk
          ? 'Будь ласка, заповніть хоча б одне поле форми або натисніть кнопку «⚡ Заповнити демо-прикладом»!'
          : 'Please fill in at least one input field or click "⚡ Fill with Demo Preset"!'
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/run-dynamic-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: agent.id,
          agent,
          formData,
          language,
          jurisdiction: isPl ? 'PL' : isDe ? 'DE' : isFr ? 'FR' : isEn ? 'EU' : 'UA',
          attachedFiles,
          userEmail: currentUserEmail || 'Unauthenticated User'
        })
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Помилка виконання агента');
      }

      setReport(data.report);
      setReportTitle(data.title || getAgentName());

      // Increment runs count
      const newRuns = runsCount + 1;
      setRunsCount(newRuns);
      try {
        localStorage.setItem('drh_dynamic_agent_runs_count', newRuns.toString());
      } catch (e) {
        console.warn(e);
      }
    } catch (err: any) {
      console.error('Dynamic agent execution error:', err);
      setError(err.message || 'Сталася помилка при генерації звіту.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2500);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setDownloadingDocx(true);
    try {
      const res = await fetch('/api/convert-md-to-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdown: report,
          title: reportTitle || getAgentName(),
          language
        })
      });

      if (!res.ok) throw new Error('Помилка генерації Word');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(reportTitle || getAgentName()).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading docx:', err);
      alert(isUk ? 'Помилка завантаження Word документа' : 'Error downloading Word document');
    } finally {
      setDownloadingDocx(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6 max-w-6xl mx-auto pb-16 font-mono text-slate-100 animate-fade-in">
        {/* Navigation & Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900/90 border border-slate-800 p-4 sm:p-5 rounded-2xl shadow-xl backdrop-blur-md">
          <div className="flex items-center gap-3">
            {onBackToHub && (
              <button
                onClick={onBackToHub}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{hubTitle ? `← ${hubTitle}` : isUk ? '← Назад до Хабу' : isPl ? '← Powrót do Centrum' : '← Back to Hub'}</span>
              </button>
            )}

            {onGoHome && (
              <button
                onClick={onGoHome}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                <Home className="w-4 h-4" />
                <span>{isUk ? '🏠 На головну' : isPl ? '🏠 Strona Główna' : '🏠 Home'}</span>
              </button>
            )}
          </div>

          {/* Pricing & Free Run Badge */}
          <div className="flex items-center gap-2">
            {runsCount === 0 ? (
              <span className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                <span>{isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : isPl ? '🎁 1. uruchomienie GRATIS' : '🎁 1st Run 100% Free'}</span>
              </span>
            ) : (
              <span className="px-3 py-1.5 bg-blue-500/20 text-blue-300 border border-blue-400/40 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-blue-400" />
                <span>{isUk ? '💳 Тариф: 50 грн (або 250 грн/міс)' : isPl ? '💳 Taryfa: 9 PLN (lub 49 PLN/mies)' : '💳 Price: €3 (or €19/mo)'}</span>
              </span>
            )}
          </div>
        </div>

        {/* Hero Card */}
        <div className="bg-gradient-to-br from-slate-900 via-indigo-950/80 to-slate-900 border-2 border-emerald-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden space-y-4">
          <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl text-emerald-400 shrink-0 shadow-lg">
              <Bot className="w-8 h-8 animate-pulse" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-2.5 py-0.5 text-[10px] bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded uppercase font-bold tracking-wider">
                  ⚡ Dynamic AI Engine 2026 (On-The-Fly)
                </span>
                <span className="px-2.5 py-0.5 text-[10px] bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded uppercase font-bold tracking-wider">
                  {agent.categoryUk || 'СПЕЦІАЛІЗОВАНИЙ МОДУЛЬ'}
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight uppercase drop-shadow-md">
                {getAgentName()}
              </h1>

              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans max-w-4xl">
                {getAgentDescription()}
              </p>
            </div>
          </div>

          {/* Quick 1-Click Demo Fill & Actions */}
          <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-800/80 flex-wrap">
            <button
              onClick={handleFillDemo}
              className="px-4 py-2 bg-gradient-to-r from-amber-500/20 to-amber-600/30 hover:from-amber-500/30 hover:to-amber-600/40 text-amber-300 border border-amber-400/40 font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>{isUk ? '⚡ Заповнити демо-прикладом у 1 клік' : isPl ? '⚡ Wypełnij danymi demo w 1 kliknięciu' : '⚡ 1-Click Fill Demo Preset'}</span>
            </button>

            {Object.keys(formData).length > 0 && (
              <button
                onClick={handleClear}
                className="px-3.5 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isUk ? 'Очистити поля' : 'Clear fields'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Dynamic Form Inputs Container */}
        <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-xs sm:text-sm font-extrabold text-emerald-300 uppercase tracking-wide flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-400" />
              <span>{isUk ? 'ВХІДНІ ПАРАМЕТРИ ТА ВИХІДНІ ДАНІ ДЛЯ АНАЛІЗУ:' : 'INPUT PARAMETERS & SOURCE DATA:'}</span>
            </h3>
            <span className="text-[10px] text-slate-400 font-mono">
              {isUk ? 'Поля чисті за замовчуванням' : 'Clean fields by default'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {(agent.fields || []).map((field) => (
              <div key={field.id} className={`space-y-2 ${field.type === 'textarea' ? 'md:col-span-2' : ''}`}>
                <label className="block text-xs font-bold text-slate-200 uppercase tracking-wide flex items-center justify-between">
                  <span>{getFieldLabel(field)}</span>
                  <span className="text-[10px] text-slate-500 font-normal">
                    {field.type === 'textarea' ? (isUk ? 'Багаторядкове поле' : 'Multi-line') : (isUk ? 'Текстове поле' : 'Text input')}
                  </span>
                </label>

                {field.type === 'textarea' ? (
                  <textarea
                    rows={4}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    placeholder={getFieldPlaceholder(field)}
                    className="w-full p-3.5 bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 transition-all font-mono shadow-inner"
                  />
                ) : field.type === 'select' && field.options && field.options.length > 0 ? (
                  <select
                    value={formData[field.id] || ''}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    className="w-full p-3.5 bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-xs sm:text-sm text-white transition-all font-mono"
                  >
                    <option value="">{isUk ? '-- Оберіть варіант із списку --' : '-- Select option --'}</option>
                    {field.options.map((opt, idx) => (
                      <option key={idx} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData[field.id] || ''}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    placeholder={getFieldPlaceholder(field)}
                    className="w-full p-3.5 bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 transition-all font-mono shadow-inner"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Multi-file Upload Zone */}
          <div className="space-y-3 pt-2">
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center justify-between">
              <span>{isUk ? '📎 Прикріпити робочі файли (PDF, Word .docx, Excel .xlsx, CSV, TXT)' : '📎 Attach Source Documents'}</span>
              <span className="text-[10px] text-slate-500 font-mono">До 25 МБ на файл</span>
            </label>

            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
                className="hidden"
                id="dynamic-file-upload"
              />
              <label
                htmlFor="dynamic-file-upload"
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-emerald-500/40 rounded-xl text-xs font-extrabold uppercase transition-all shadow-md flex items-center gap-2 cursor-pointer"
              >
                <Upload className="w-4 h-4 text-emerald-400" />
                <span>{isUk ? 'Завантажити документ(и)' : 'Upload document(s)'}</span>
              </label>

              {attachedFiles.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {attachedFiles.map((file, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-slate-950 border border-slate-700 text-slate-300 text-xs rounded-lg flex items-center gap-2 font-mono"
                    >
                      <FileText className="w-3.5 h-3.5 text-blue-400" />
                      <span className="max-w-[180px] truncate">{file.name}</span>
                      <button
                        onClick={() => handleRemoveFile(idx)}
                        className="text-slate-500 hover:text-red-400 transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-950/80 border border-red-500/50 rounded-xl text-red-200 text-xs flex items-center gap-3 font-mono animate-fade-in">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Action Launch Button */}
          <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-xs text-slate-400 font-mono">
              {runsCount === 0
                ? (isUk ? '✨ Ваш 1-й запуск повністю безкоштовний.' : '✨ Your 1st run is completely free.')
                : (isUk ? '💳 Стандартна вартість разового запуску: 50 грн' : '💳 Price: €3 / 9 PLN / 50 UAH')}
            </div>

            <button
              onClick={handleExecute}
              disabled={isLoading}
              className="px-8 py-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-sm uppercase rounded-xl transition-all shadow-2xl flex items-center gap-2.5 cursor-pointer disabled:opacity-50 tracking-wider"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin text-white" />
                  <span>{loadingSteps[loadingStepIndex]}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 text-amber-300 animate-pulse" />
                  <span>{isUk ? '🚀 Сформувати Офіційний Документ' : isPl ? '🚀 Sporządź Oficjalny Dokument' : '🚀 Generate Official Document'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results Canvas */}
        {report && (
          <div className="bg-slate-900/95 border-2 border-emerald-500/50 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-extrabold text-white uppercase tracking-wide">
                    {reportTitle || (isUk ? 'ОФІЦІЙНИЙ ВИСНОВОК ТА ЗВІТ' : 'OFFICIAL REPORT')}
                  </h3>
                  <p className="text-xs text-emerald-400 font-mono">
                    ✓ {isUk ? 'Сформовано за нормативними стандартами ДСТУ / EU' : 'Generated under regulatory compliance'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleCopyReport}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
                >
                  {copiedReport ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                  <span>{copiedReport ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Копіювати звіт' : 'Copy')}</span>
                </button>

                <button
                  onClick={handleDownloadDocx}
                  disabled={downloadingDocx}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {downloadingDocx ? <RefreshCw className="w-4 h-4 animate-spin text-white" /> : <Download className="w-4 h-4 text-white" />}
                  <span>{isUk ? 'Завантажити Word (.docx)' : isPl ? 'Pobierz Word (.docx)' : 'Download Word (.docx)'}</span>
                </button>
              </div>
            </div>

            {/* Markdown Report Render Body */}
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 sm:p-7 text-xs sm:text-sm text-slate-200 leading-relaxed font-mono whitespace-pre-wrap selection:bg-emerald-500 selection:text-black max-h-[750px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800">
              {report}
            </div>

            {/* Compliance Disclaimer */}
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-[11px] text-slate-400 leading-relaxed font-sans flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-slate-300">
                  {isUk ? 'Юридичне застереження та стандарт обачності:' : 'Compliance & Due Diligence Statement:'}
                </span>{' '}
                {isUk
                  ? 'Сформований звіт створено автоматизованим експертним модулем на основі наданих вихідних даних. Документ містить висновки, матрицю ризиків та рекомендації для використання у професійній діяльності.'
                  : 'The generated report was prepared by the automated expert module. Use in accordance with applicable laws.'}
              </div>
            </div>
          </div>
        )}

        {/* Regional Payment Modal when free trial is exhausted */}
        {isPaymentModalOpen && (
          <RegionalPaymentModal
            isOpen={isPaymentModalOpen}
            onClose={() => setIsPaymentModalOpen(false)}
            agentId={agent.id}
            agentName={getAgentName()}
            region={isPl ? 'PL' : isDe ? 'DE' : isFr ? 'FR' : isEn ? 'EU' : 'UA'}
            language={language}
            currency={isPl ? 'PLN' : isDe || isFr || isEn ? 'EUR' : 'UAH'}
            defaultPrice={isPl ? 49 : isDe || isFr || isEn ? 19 : 250}
            defaultSinglePrice={isPl ? 9 : isDe || isFr || isEn ? 3 : 50}
            currentUserEmail={currentUserEmail}
            onSuccess={() => {
              setIsPaymentModalOpen(false);
              handleExecute();
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default DynamicOnTheFlyAgentView;
